import {
  GetAnalyticsExtSiteSettingsRequest,
  IGetAnalyticsExtSiteSettingsResult
} from '~src/settings/extensions-settings/serverApi/requests/analytics/GetAnalyticsExtSiteSettingsRequest';
import {
  IUpdatePrepExtensionsServerSettingsParams,
  UpdatePrepExtensionsServerSettingsRequest
} from '~src/settings/extensions-settings/serverApi/requests/prep/UpdatePrepExtensionsServerSettingsRequest';
import {IPrepExtensionsServerSettings} from '~src/settings/types/ISiteSettingsForSiteAdmin';

import { IUpdateScheduleFrequencyServerSettingResult, UpdateScheduleFrequencyServerSettingRequest } from '../../backgroundJob/types/UpdateScheduleFrequencyServerSettingRequest';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import TagHttpClient from '../../httpClient/TAG/TagHttpClient';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { GetLastRecommendationTrainTimeRequest, IGetLastRecommendationTrainTimeResult } from '../../recommendations/serverApi/requests/GetLastRecommendationTrainTimeRequest';
import { IScheduleFrequency } from '../../schedule/types/ISchedule';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { GetLastActiveDirectoryGroupsSyncTimeRequest, IGetLastActiveDirectoryGroupsSyncTimeResult } from '../../userManagement/groups/serverApi/requests/getLastActiveDirectoryGroupsSyncTimeRequest';
import { UpdateExtsvcServerSettingsRequest } from '../extensions-settings/serverApi/requests/analytics/UpdateExtsvcServerSettingsRequest';
import { GetExtensionsServerSettingsRequest, IGetExtensionsServerSettingsResult } from '../extensions-settings/serverApi/requests/dashboard/GetExtensionsServerSettingsRequest';
import { IUpdateExtensionsServerSettingsParams, UpdateExtensionsServerSettingsRequest } from '../extensions-settings/serverApi/requests/dashboard/UpdateExtensionsServerSettingsRequest';
import { GetExtsvcServerSettingsRequest, IGetExtsvcServerSettingsResult } from '../extensions-settings/serverApi/requests/GetExtsvcServerSettingsRequest';
import { GetServerSettingsRequest, IGetServerSettingsResult } from '../ServerApi/requests/serverSettings/GetServerSettingsRequest';
import { SetRunNowForSitesRequest } from '../ServerApi/requests/serverSettings/SetRunNowForSitesRequest';
import { ISetSandboxForAllSitesParams, ISetSandboxForAllSitesResult, SetSandboxSettingsForAllSitesRequest } from '../ServerApi/requests/serverSettings/SetSandboxSettingsForAllSitesRequest';
import { IUpdateBooleanServerSettingResult, UpdateBooleanServerSettingRequest } from '../ServerApi/requests/serverSettings/UpdateBooleanServerSettingRequest';
import { IUpdateStringServerSettingResult, UpdateStringServerSettingRequest } from '../ServerApi/requests/serverSettings/UpdateStringServerSettingRequest';
import {
  IDashExtServerSettings,
  IEditableServerSettings,
  IExtsvcServerSettings
} from '../ServerApi/types/IEditableServerSettings';

export interface IServerTimestamps {
  lastRecommendationTrainTime: string;
  lastActiveDirectoryGroupsSyncTime: string;
}

export class ServerSettings {

  public fetch(): Promise<IEditableServerSettings> {
    return WebHttpClient.sendRequest(new GetServerSettingsRequest({}))
      .then((result: IGetServerSettingsResult) => result.serverSettings);
  }

  public getExtensionsSettings(): Promise<IDashExtServerSettings> {
    return WebHttpClient.sendRequest(new GetExtensionsServerSettingsRequest({}))
      .then((result: IGetExtensionsServerSettingsResult) => result);
  }

  public getExtsvcSettings(): Promise<IExtsvcServerSettings> {
    return TagHttpClient.sendTagRequest(new GetExtsvcServerSettingsRequest())
      .then((result: IGetExtsvcServerSettingsResult) => result);
  }

  public getAnalyticsSiteLevel() {
    return WebHttpClientWithErrorToastInstance.sendTagRequest(new GetAnalyticsExtSiteSettingsRequest())
      .then((result: IGetAnalyticsExtSiteSettingsResult) => result);
  }

  public getAnalyticsServerLevel() {
    return WebHttpClientWithErrorToastInstance.sendTagRequest(new GetExtsvcServerSettingsRequest())
      .then((result: IGetExtsvcServerSettingsResult) => result);
  }

  public getTimestamps(): Promise<IServerTimestamps> {
    const timestamps: IServerTimestamps =
      {
        lastRecommendationTrainTime: null,
        lastActiveDirectoryGroupsSyncTime: null
      };
    return WebHttpClient.sendRequest(new GetLastRecommendationTrainTimeRequest())
      .then(
        (result: IGetLastRecommendationTrainTimeResult) => {
          timestamps.lastRecommendationTrainTime = result.time;
          return WebHttpClient.sendRequest(new GetLastActiveDirectoryGroupsSyncTimeRequest())
            .then((result: IGetLastActiveDirectoryGroupsSyncTimeResult) => {
              timestamps.lastActiveDirectoryGroupsSyncTime = result.time;
              return timestamps;
            });
          });
  }

  public updateBoolean(setting: string, value: boolean): Promise<IUpdateBooleanServerSettingResult> {
    const request = new UpdateBooleanServerSettingRequest({
      setting: setting,
      value: value
    });
    return WebHttpClient.sendRequest(request);
  }

  public updateString(setting: string, value: string): Promise<IUpdateStringServerSettingResult> {
    const request = new UpdateStringServerSettingRequest({
      setting: setting,
      value: value
    });
    return WebHttpClient.sendRequest(request);
  }

  public updateScheduleFrequency(setting: string, value: IScheduleFrequency): Promise<IUpdateScheduleFrequencyServerSettingResult> {
    const request = new UpdateScheduleFrequencyServerSettingRequest({
      setting: setting,
      value: value
    });
    return WebHttpClient.sendRequest(request);
  }

  public updateExtensionsSettings(extensionsSettings: IDashExtServerSettings): Promise<IResult> {
    const params: IUpdateExtensionsServerSettingsParams = {
      settings: extensionsSettings
    };
    return getErrorNotifyingServer().sendRequest(new UpdateExtensionsServerSettingsRequest(params));
  }

  public updatePrepServerExtensionsSettingsForServerAdmin(prepExtensionsSettings: IPrepExtensionsServerSettings): Promise<IResult> {
    const params: IUpdatePrepExtensionsServerSettingsParams = {
      prepExtensionsServerSettings: prepExtensionsSettings
    };
    return getErrorNotifyingServer().sendRequest(new UpdatePrepExtensionsServerSettingsRequest(params));
  }

  public updateExtsvSettings(extsvcSettings: IExtsvcServerSettings): Promise<IResult> {
    return getErrorNotifyingServer().sendTagRequest(new UpdateExtsvcServerSettingsRequest(extsvcSettings));
  }

  public updateRunNowSettings(sites: string[], shouldEnable: boolean): Promise<IResult> {
    return getErrorNotifyingServer().sendRequest(new SetRunNowForSitesRequest({sites, shouldEnable}));
  }

  public setSandboxSettingForExistingSites(shouldEnable: boolean): Promise<ISetSandboxForAllSitesResult> {
    const params: ISetSandboxForAllSitesParams = {
      shouldEnable: shouldEnable
    };
    return WebHttpClient.sendRequest(new SetSandboxSettingsForAllSitesRequest(params));
  }

  public updateAnalyticsExtensionsServerSettings(analyticsExtensionsServerSettingsEnabled: IExtsvcServerSettings) {
    return WebHttpClientWithErrorToastInstance.sendTagRequest(new UpdateExtsvcServerSettingsRequest(analyticsExtensionsServerSettingsEnabled));
  }
}

function getSessionManagementService() {
  return getSessionManagementServiceInstance();
}

function getErrorNotifyingServer() {
  return WebHttpClientWithErrorToastInstance;
}

export const ServerSettingsInstance = new ServerSettings();
